import React, { useState, useEffect, useRef } from "react";
import { BookOpen, Users, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const links = [
  { 
    title: "Portal", 
    icon: <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500" />, 
    href: "/portal", 
    gradient: "from-yellow-400 to-yellow-600",
    ariaLabel: "Access student portal"
  },
  { 
    title: "Apply Now", 
    icon: <Users className="w-8 h-8 sm:w-10 sm:h-10 text-pink-500" />, 
    href: "/apply", 
    gradient: "from-pink-400 to-pink-600",
    ariaLabel: "Apply for admission"
  },
  { 
    title: "Library", 
    icon: <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500" />, 
    href: "/library", 
    gradient: "from-blue-400 to-blue-600",
    ariaLabel: "Visit library resources"
  },
  { 
    title: "Alumni", 
    icon: <ExternalLink className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />, 
    href: "/alumni", 
    gradient: "from-green-400 to-green-600",
    ariaLabel: "Connect with alumni network"
  },
];

const containerVariants = { 
  hidden: {}, 
  visible: { transition: { staggerChildren: 0.15 } } 
};

const cardVariants = { 
  hidden: { opacity: 0, y: 40 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
};

// Individual card component to isolate ripple state
const QuickLinkCard = ({ link, index }) => {
  const [ripples, setRipples] = useState([]);
  const timeoutRefs = useRef([]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(clearTimeout);
    };
  }, []);

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const size = rect.width;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    const newRipple = { x, y, size, id: Date.now() };
    setRipples((prev) => [...prev, newRipple]);

    // Store timeout ref for cleanup
    const timeoutId = setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);
    
    timeoutRefs.current.push(timeoutId);
  };

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ y: -5, scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Link
        to={link.href}
        onClick={handleClick}
        aria-label={link.ariaLabel}
        className="relative group w-full rounded-xl sm:rounded-2xl bg-white shadow-md hover:shadow-2xl transition-all duration-300 p-4 sm:p-6 md:p-8 flex flex-col items-center overflow-hidden touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 block"
      >
        {/* Gradient overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${link.gradient} opacity-0 group-hover:opacity-10 rounded-xl sm:rounded-2xl transition duration-300`}
        />
        
        {/* Ripple effects - Now isolated per card */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {ripples.map((r) => (
            <span
              key={r.id}
              className="absolute rounded-full bg-yellow-200 opacity-30 animate-ping"
              style={{
                left: r.x,
                top: r.y,
                width: r.size,
                height: r.size,
              }}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="mb-3 sm:mb-4 relative z-10" aria-hidden="true">
          {link.icon}
        </div>

        {/* Title */}
        <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-800 relative z-10 text-center">
          {link.title}
        </h3>
      </Link>
    </motion.div>
  );
};

const QuickLinks = () => {
  const [orbCount, setOrbCount] = useState(10);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const updateOrbCount = () => {
      const width = window.innerWidth;
      if (width < 640) setOrbCount(5);
      else if (width < 1024) setOrbCount(8);
      else setOrbCount(12);
    };

    updateOrbCount();
    window.addEventListener("resize", updateOrbCount);
    
    return () => window.removeEventListener("resize", updateOrbCount);
  }, []);

  // Reduce orbs if user prefers reduced motion
  const effectiveOrbCount = prefersReducedMotion ? Math.min(orbCount, 4) : orbCount;

  const orbs = Array.from({ length: effectiveOrbCount }, (_, i) => ({
    id: i,
    size: 10 + Math.random() * 25,
    top: 10 + Math.random() * 80,
    left: 10 + Math.random() * 80,
    hue: Math.floor(Math.random() * 360),
    blur: Math.random() * 12 + 4,
  }));

  return (
    <section 
      className="relative w-full py-12 sm:py-16 md:py-20 bg-gradient-to-br from-white via-sky-50 to-sky-100 overflow-hidden"
      aria-labelledby="quicklinks-heading"
    >
      {/* Floating orbs container */}
      {!prefersReducedMotion && (
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none" aria-hidden="true">
          {orbs.map((orb) => (
            <motion.div
              key={orb.id}
              className="absolute rounded-full"
              style={{
                width: `${orb.size}px`,
                height: `${orb.size}px`,
                top: `${orb.top}%`,
                left: `${orb.left}%`,
                background: `hsl(${orb.hue}, 70%, 75%)`,
                filter: `blur(${orb.blur}px)`,
                opacity: 0.35,
              }}
              animate={{
                y: ["0%", "8%", "-4%", "0%"],
                x: ["0%", "4%", "-4%", "0%"],
                rotate: [0, 6, -6, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      )}

      {/* Main content container */}
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          className="text-center mb-8 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h2 
            id="quicklinks-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-800 leading-tight"
          >
            Explore Our Quick Links
          </h2>
        </motion.div>

        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          role="list"
        >
          {links.map((link, index) => (
            <div key={index} role="listitem">
              <QuickLinkCard link={link} index={index} />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default QuickLinks;